import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import { Button, Progress } from 'antd';
import { connect } from 'react-redux';

import { FormatedMoney } from '@atoms/table';
import RenderInlinePreviewItem from '@molecules/formater/TextPreviewInlineFormatter';
import notification from '@helps/notification';
import TfaConfirm from '@organisms/exchange/TfaConfirm';
import NewExchange from '@organisms/exchange/NewExchange';

import { responseError } from 'FIELDS';
import { tConsume } from 'TRANSLATION';
import defaults from 'DEFAULTS';
import api from 'API';
import { Strings } from 'HELPERS';

class ConfirmExchange extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      waitingForCode: '',
      timeLeft: 0,
      progress: 100,
      timeExpired: false,
      amendRequest: false,
      rateData: props.rateData,
    };

    this._mounted = false;
    this.timer = null;

    this.responseError = responseError.bind(this, this);

    this.startCountdown = this.startCountdown.bind(this);
    this.stopCountdown = this.stopCountdown.bind(this);
    this.resetCountdown = this.resetCountdown.bind(this);
    this.updateCountdown = this.updateCountdown.bind(this);
    this.createOrder = this.createOrder.bind(this);
    this.handleRetry = this.handleRetry.bind(this);
  }

  componentDidMount() {
    const { rateData } = this.state;

    this._mounted = true;

    this.startCountdown();
    this.setState({ timeLeft: rateData.valid_till_secs });
  }

  componentWillUnmount() {
    this._mounted = false;
    this.stopCountdown();
  }

  startCountdown() {
    this.setState({ timeExpired: false });
    this.resetCountdown();
    this.timer = setInterval(this.updateCountdown, 1000);
  }

  stopCountdown() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  resetCountdown() {
    const { rateData } = this.state;

    this.setState({
      timeLeft: rateData.valid_till_secs,
      progress: 100,
    });
  }

  updateCountdown() {
    const { rateData } = this.state;

    this.setState((prevState) => {
      const timeLeft = Math.max(0, prevState.timeLeft - 1);
      const progress = (timeLeft / rateData.valid_till_secs) * 100;

      if (timeLeft === 0) {
        notification('Time for order confirmation has expired', 'error');
        this.stopCountdown();
        this.setState({ timeExpired: true });
      }

      return { timeLeft, progress };
    });
  }

  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return (
      <div style={{ transform: 'rotate(0deg) scaleX(-1)' }}>
        {minutes.toString().padStart(2, '0')}:{remainingSeconds.toString().padStart(2, '0')}
      </div>
    );
  }

  async createOrder() {
    const { loading, rateData } = this.state;
    const { getData, toggleModal, userType, vendor } = this.props;

    if (!loading) {
      this.setState({ loading: true });

      const data = {
        temporary_order_id: rateData?.id,
        vendor: vendor,
      };

      try {
        let CONFIG;

        if (userType === 'aemi') {
          CONFIG = api.banking.client.aemiExchanges(rateData?.id).AXIOS('POST');
        } else {
          CONFIG = api.banking.client.exchanges().AXIOS('POST', data);
        }

        const result = await axios(CONFIG);

        if (this._mounted) {
          if (result.data.message.auth_type) {
            this.setState({
              data: data,
              waitingForCode: result.data.message.auth_type,
            });
          } else {
            notification(result.data.message, 'success');
            getData();
            toggleModal();
          }
        }
      } catch (error) {
        if (this._mounted) {
          this.responseError(error);
        }
      } finally {
        if (this._mounted) {
          this.setState({ loading: false });
        }
      }
    }
  }

  handleRetry() {
    this.stopCountdown();
    this.setState({ amendRequest: true });
  }

  async handleRepeat() {
    const { userType, vendor, data } = this.props;
    this.setState({ loading: true });
    const { currency_pair, ...rest } = data;
    const _data = {
      ...rest,
      amount: Number(rest.amount.replace(/ /g, '')),
      currency_from: currency_pair.split('/')[0],
      currency_to: currency_pair.split('/')[1],
    };

    try {
      let CONFIG;

      if (userType === 'aemi') {
        CONFIG = api.banking.client.aemiCalculation().AXIOS('POST', _data);
      } else {
        CONFIG = api.banking.client.calculation().AXIOS('POST', { ..._data, vendor });
      }

      const result = await axios(CONFIG);
      if (this._mounted) {
        this.setState({
          rateData: result.data.data,
        });
        this.startCountdown();
      }
    } catch (error) {
      if (this._mounted) {
        this.responseError(error);
      }
    } finally {
      if (this._mounted) {
        this.setState({ loading: false });
      }
    }
  }

  render() {
    const { t, currencies, userType, getData, toggleModal, vendor, data: repeatData } = this.props;
    const { timeLeft, progress, waitingForCode, data, timeExpired, amendRequest, rateData } = this.state;
    if (waitingForCode) {
      return (
        <TfaConfirm
          toggleModal={toggleModal}
          userType={userType}
          updateTransation={getData}
          tfaMethod={waitingForCode}
          data={data}
        />
      );
    }

    if (amendRequest) {
      return (
        <NewExchange
          userType={userType}
          getData={getData}
          vendor={vendor}
          data={repeatData}
          forceOpen={true}
          onModalClose={toggleModal}
        />
      );
    }

    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 30,
            transform: 'rotate(360deg) scaleX(-1)',
          }}
        >
          <Progress
            type={'circle'}
            strokeColor={{
              '0%': '#F5222D',
              '100%': '#FFB33C',
            }}
            percent={progress}
            format={() => this.formatTime(timeLeft)}
          />
        </div>
        {RenderInlinePreviewItem(
          Strings.Compile(t('fieldsGroup.original_rate.title'), [
            `${rateData?.currency_from}/${rateData?.currency_to}`,
          ]),
          rateData?.original_rate,
          9,
          7,
          16,
          true
        )}
        {RenderInlinePreviewItem(t('fieldsGroup.client_mark_up.title'), `${rateData?.client_margin}%`, 9, 7, 16, true)}
        {RenderInlinePreviewItem(
          Strings.Compile(t('fieldsGroup.fixed_rate.title'), [`${rateData?.currency_from}/${rateData?.currency_to}`]),
          rateData?.fixed_rate,
          9,
          7,
          16,
          true
        )}
        {RenderInlinePreviewItem(
          Strings.Compile(t('fieldsGroup.total_amount_from.title'), [`${rateData?.currency_from}`]),
          <FormatedMoney
            item={{
              amount: rateData?.amount_from,
              currency: rateData?.currency_from,
              direction: 'PAYOUT',
            }}
            list={currencies}
          />,
          9,
          7,
          16,
          true
        )}
        {RenderInlinePreviewItem(
          Strings.Compile(t('fieldsGroup.total_amount_to.title'), [`${rateData?.currency_to}`]),
          <FormatedMoney
            item={{
              amount: rateData?.amount_to,
              currency: rateData?.currency_to,
              direction: 'PAYIN',
            }}
            list={currencies}
          />,
          9,
          7,
          16,
          true
        )}
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 10 }}>
          <Button type={'danger'} size={defaults.antd.button.size} onClick={toggleModal} block>
            {t('button.cancel')}
          </Button>
          <Button type={'primary'} size={defaults.antd.button.size} onClick={() => this.handleRetry()} block>
            {t('button.amendRequest')}
          </Button>
          {timeExpired && (
            <>
              <Button type={'primary'} size={defaults.antd.button.size} onClick={() => this.handleRepeat()} block>
                {t('button.repeatRequest')}
              </Button>
            </>
          )}
          <Button
            type={'primary'}
            size={defaults.antd.button.size}
            onClick={() => this.createOrder()}
            disabled={timeExpired}
            block
          >
            {t('button.confirm')}
          </Button>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  currencies: state.currencies.list,
});

export default tConsume(withRouter(connect(mapStateToProps)(ConfirmExchange)));

ConfirmExchange.propTypes = {
  currencies: PropTypes.array.isRequired,
  rateData: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  userType: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  toggleModal: PropTypes.func,
  getData: PropTypes.func,
  vendor: PropTypes.string,
};
